import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, IconButton, Tooltip, InputLabel, Collapse, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import { useCart } from '../contexts/CartContext';
import { 
  Restaurant, 
  LocalDining, 
  SetMeal, 
  Nature, 
  Circle, 
  Kitchen, 
  Whatshot, 
  RiceBowl, 
  LocalPizza, 
  Fastfood, 
  Cake, 
  EmojiFoodBeverage, 
  Spa, 
  LocalCafe,
  Store,
  Search,
  Upload,
  LocationOn,
  Close,
  LocalMall,
  RestaurantMenu,
  ExpandLess,
  ExpandMore,
  BakeryDining,
  LunchDining,
  DinnerDining,
  LocalBar,
  Icecream
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const PrescriptionUpload = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(45deg, #e3f2fd 30%, #bbdefb 90%)',
  border: '1px dashed #1e88e5',
}));

const FoodCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
  gap: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for food stores
const DefaultRestaurantLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

// Food categories with hierarchical structure
const restaurantFoods = [
  {
    category: { bangla: "রুটি ও ভাত", english: "Breads & Rice" },
    icon: <RiceBowl />,
    items: [
      { bangla: "পরোটা", english: "Paratha", icon: <BakeryDining /> },
      { bangla: "নান", english: "Naan", icon: <BakeryDining /> },
      { bangla: "চাপাটি", english: "Chapati", icon: <BakeryDining /> },
      { bangla: "সাদা ভাত", english: "Plain Rice", icon: <RiceBowl /> },
      { bangla: "পোলাও", english: "Polao", icon: <RiceBowl /> },
    ]
  },
  {
    category: { bangla: "মাংস", english: "Meat Dishes" },
    icon: <Restaurant />,
    items: [
      { bangla: "বিফ রেজালা", english: "Beef Rezala", icon: <LunchDining /> },
      { bangla: "মাটন কারি", english: "Mutton Curry", icon: <DinnerDining /> },
      { bangla: "চিকেন রোস্ট", english: "Chicken Roast", icon: <Restaurant /> },
      { bangla: "চিকেন কোরমা", english: "Chicken Korma", icon: <LocalDining /> },
      { bangla: "কাবাব", english: "Kebab", icon: <Restaurant /> },
    ]
  },
  {
    category: { bangla: "মাছ", english: "Fish Dishes" },
    icon: <SetMeal />,
    items: [
      { bangla: "ইলিশ ভুনা", english: "Hilsa Curry", icon: <SetMeal /> },
      { bangla: "রুই মাছ ভাজি", english: "Fried Rui Fish", icon: <DinnerDining /> },
      { bangla: "চিংড়ি মালাইকারি", english: "Prawn Malai Curry", icon: <SetMeal /> },
    ]
  },
  {
    category: { bangla: "সবজি ও ডাল", english: "Vegetables & Lentils" },
    icon: <Nature />,
    items: [
      { bangla: "বেগুন ভাজি", english: "Fried Eggplant", icon: <Nature /> },
      { bangla: "শাক ভাজি", english: "Fried Greens", icon: <Nature /> },
      { bangla: "ডাল", english: "Lentil Soup", icon: <EmojiFoodBeverage /> },
      { bangla: "আলু ভর্তা", english: "Mashed Potato", icon: <Kitchen /> },
    ]
  },
  {
    category: { bangla: "স্ন্যাকস ও স্ট্রিট ফুড", english: "Snacks & Street Food" },
    icon: <Fastfood />,
    items: [
      { bangla: "সিঙ্গারা", english: "Singara", icon: <BakeryDining /> },
      { bangla: "সমুচা", english: "Samosa", icon: <BakeryDining /> },
      { bangla: "চটপটি", english: "Chotpoti", icon: <Fastfood /> },
      { bangla: "ফুচকা", english: "Fuchka", icon: <Fastfood /> },
      { bangla: "রোল", english: "Roll", icon: <LocalDining /> },
    ]
  },
  {
    category: { bangla: "মিষ্টি", english: "Sweets" },
    icon: <Cake />,
    items: [
      { bangla: "রসগোল্লা", english: "Rasgulla", icon: <Cake /> },
      { bangla: "চমচম", english: "Chomchom", icon: <Icecream /> },
      { bangla: "সন্দেশ", english: "Sandesh", icon: <Cake /> },
      { bangla: "মিষ্টি দই", english: "Sweet Yogurt", icon: <Icecream /> },
    ]
  },
  {
    category: { bangla: "ফাস্ট ফুড", english: "Fast Food" },
    icon: <LocalPizza />,
    items: [
      { bangla: "বার্গার", english: "Burger", icon: <LocalDining /> },
      { bangla: "পিজা", english: "Pizza", icon: <LocalPizza /> },
      { bangla: "ফ্রাইড চিকেন", english: "Fried Chicken", icon: <Restaurant /> },
      { bangla: "স্যান্ডউইচ", english: "Sandwich", icon: <LocalDining /> },
      { bangla: "ফ্রেঞ্চ ফ্রাই", english: "French Fries", icon: <Fastfood /> },
      { bangla: "পাস্তা", english: "Pasta", icon: <LocalDining /> },
      { bangla: "হটডগ", english: "Hotdog", icon: <Fastfood /> },
    ]
  }
];

const stores = [
  {
    name: 'রসগোল্লা রেস্তোরাঁ',
    englishName: 'Rasgolla Restaurant',
    logo: '/images/logos/rasgolla.png',
    description: 'বিশুদ্ধ ও স্বাস্থ্যকর খাবারের জন্য বিখ্যাত'
  },
  {
    name: 'ঢাকা বিরিয়ানি হাউস',
    englishName: 'Dhaka Biryani House',
    logo: '/images/logos/biryani.png',
    description: 'ঐতিহ্যবাহী বিরিয়ানি ও মাংসের খাবার'
  },
  {
    name: 'বাংলা রান্নাঘর',
    englishName: 'Bangla Kitchen',
    logo: '/images/logos/bangla.png',
    description: 'দেশীয় খাবারের স্বাদে অতুলনীয়'
  },
  {
    name: 'মামার হোটেল',
    englishName: 'Mama Hotel',
    logo: '/images/logos/mama.png',
    description: 'সাশ্রয়ী ও সুস্বাদু খাবার'
  },
  {
    name: 'স্বাদ রেস্তোরাঁ',
    englishName: 'Swad Restaurant',
    logo: '/images/logos/swad.png',
    description: 'বৈচিত্র্যময় খাবারের সমাহার'
  },
  {
    name: 'পপুলার ক্যাটারিং',
    englishName: 'Popular Catering',
    logo: '/images/logos/popular-catering.png',
    description: 'অনুষ্ঠানের জন্য বিশেষ খাবার সেবা'
  },
  {
    name: 'দেশী খাবার ঘর',
    englishName: 'Deshi Food House',
    logo: '/images/logos/deshi.png',
    description: 'গ্রামীণ স্বাদের খাবার'
  },
  {
    name: 'ফুড কর্নার',
    englishName: 'Food Corner',
    logo: '/images/logos/food-corner.png',
    description: 'দ্রুত সেবা ও তাজা খাবার'
  },
];

const foods = [
  {
    id: 1,
    name: 'চিকেন বিরিয়ানি',
    englishName: 'Chicken Biryani',
    category: 'বিরিয়ানি ও পোলাও',
    store: 'ঢাকা বিরিয়ানি হাউস',
    price: '৳২৫০.০০',
    image: '/images/foods/biryani1.png',
    description: 'বাসমতি চালের সাথে রান্না করা সুগন্ধি চিকেন বিরিয়ানি। ঐতিহ্যবাহী মসলা ও দেশী ঘি দিয়ে প্রস্তুত।',
    restaurant: 'ঢাকা বিরিয়ানি হাউস'
  },
  {
    id: 2,
    name: 'ইলিশ মাছের তরকারি',
    englishName: 'Hilsa Fish Curry',
    category: 'মাছ ও সামুদ্রিক খাবার',
    store: 'বাংলা রান্নাঘর',
    price: '৳৪৫০.০০',
    image: '/images/foods/fish1.png',
    description: 'তাজা ইলিশ মাছ দিয়ে রান্না করা ঐতিহ্যবাহী বাঙালি তরকারি। পেঁয়াজ, আদা-রসুন ও মসলার সুগন্ধে ভরপুর।',
    restaurant: 'বাংলা রান্নাঘর'
  },
  {
    id: 3,
    name: 'গরুর মাংসের কোর্মা',
    englishName: 'Beef Korma',
    category: 'মাংসের খাবার',
    store: 'রসগোল্লা রেস্তোরাঁ',
    price: '৳৩২০.০০',
    image: '/images/foods/beef1.png',
    description: 'নরম গরুর মাংস দই ও মসলার সাথে ধীরে ধীরে রান্না করা। ভাত বা রুটির সাথে পরিবেশনের জন্য আদর্শ।',
    restaurant: 'রসগোল্লা রেস্তোরাঁ'
  },
  {
    id: 4,
    name: 'মসুর ডাল',
    englishName: 'Red Lentil Dal',
    category: 'ডাল',
    store: 'মামার হোটেল',
    price: '৳৮০.০০',
    image: '/images/foods/dal1.png',
    description: 'পুষ্টিকর মসুর ডাল হলুদ, পেঁয়াজ ও মসলা দিয়ে রান্না। প্রোটিনের চমৎকার উৎস।',
    restaurant: 'মামার হোটেল'
  },
  {
    id: 5,
    name: 'আলু ভর্তা',
    englishName: 'Mashed Potato',
    category: 'ভর্তা',
    store: 'দেশী খাবার ঘর',
    price: '৳৬০.০০',
    image: '/images/foods/aloo1.png',
    description: 'সিদ্ধ আলু সরিষার তেল, পেঁয়াজ ও কাঁচামরিচ দিয়ে ভর্তা করা। গরম ভাতের সাথে খেতে অসাধারণ।',
    restaurant: 'দেশী খাবার ঘর'
  },
  {
    id: 6,
    name: 'সিক কাবাব',
    englishName: 'Seekh Kabab',
    category: 'কাবাব ও গ্রিল',
    store: 'স্বাদ রেস্তোরাঁ',
    price: '৳১৮০.০০',
    image: '/images/foods/kabab1.png',
    description: 'কিমা করা গরুর মাংস মসলার সাথে মিশিয়ে গ্রিল করা। রুটি ও সালাদের সাথে পরিবেশিত।',
    restaurant: 'স্বাদ রেস্তোরাঁ'
  },
  {
    id: 7,
    name: 'শাক ভাজি',
    englishName: 'Fried Greens',
    category: 'শাকসবজির খাবার',
    store: 'বাংলা রান্নাঘর',
    price: '৳৭০.০০',
    image: '/images/foods/shaak1.png',
    description: 'তাজা পালং শাক রসুন, পেঁয়াজ ও মসলা দিয়ে ভেজে রান্না। ভিটামিন ও আয়রনে ভরপুর।',
    restaurant: 'বাংলা রান্নাঘর'
  },
  {
    id: 8,
    name: 'পরোটা',
    englishName: 'Paratha',
    category: 'রুটি',
    store: 'ফুড কর্নার',
    price: '৳২৫.০০',
    image: '/images/foods/paratha1.png',
    description: 'স্তরে স্তরে তৈরি নরম ও মচমচে পরোটা। যেকোনো তরকারির সাথে খাওয়ার জন্য উপযুক্ত।',
    restaurant: 'ফুড কর্নার'
  },
  {
    id: 9,
    name: 'রসগোল্লা',
    englishName: 'Rasgulla',
    category: 'মিষ্টি ও ডেজার্ট',
    store: 'রসগোল্লা রেস্তোরাঁ',
    price: '৳১২০.০০',
    image: '/images/foods/rasgulla1.png',
    description: 'তাজা ছানা দিয়ে তৈরি নরম ও সুস্বাদু রসগোল্লা। চিনির সিরায় ভেজানো মুখরোচক মিষ্টি।',
    restaurant: 'রসগোল্লা রেস্তোরাঁ'
  },
  {
    id: 10,
    name: 'চা',
    englishName: 'Tea',
    category: 'পানীয়',
    store: 'মামার হোটেল',
    price: '৳১৫.০০',
    image: '/images/foods/tea1.png',
    description: 'দেশীয় চা পাতা দিয়ে তৈরি গরম চা। দুধ ও চিনি সহ পরিবেশিত।',
    restaurant: 'মামার হোটেল'
  },
  {
    id: 11,
    name: 'সবজি স্যুপ',
    englishName: 'Vegetable Soup',
    category: 'স্যুপ',
    store: 'স্বাদ রেস্তোরাঁ',
    price: '৳৯৫.০০',
    image: '/images/foods/soup1.png',
    description: 'মিশ্র সবজি দিয়ে তৈরি পুষ্টিকর স্যুপ। গরম গরম পরিবেশনের জন্য আদর্শ।',
    restaurant: 'স্বাদ রেস্তোরাঁ'
  },
  {
    id: 12,
    name: 'আমের আচার',
    englishName: 'Mango Pickle',
    category: 'আচার ও চাটনি',
    store: 'দেশী খাবার ঘর',
    price: '৳৫০.০০',
    image: '/images/foods/pickle1.png',
    description: 'কাঁচা আম মসলার সাথে মিশিয়ে তৈরি ঝাল আচার। ভাত ও রুটির সাথে খাওয়ার জন্য চমৎকার।',
    restaurant: 'দেশী খাবার ঘর'
  },
  {
    id: 13,
    name: 'চিকেন বার্গার',
    englishName: 'Chicken Burger',
    category: 'ফাস্ট ফুড',
    store: 'ফুড কর্নার',
    price: '৳১৫০.০০',
    image: '/images/foods/burger1.png',
    description: 'গ্রিল করা চিকেন প্যাটি, সালাদ ও সস দিয়ে তৈরি সুস্বাদু বার্গার। ফ্রেঞ্চ ফ্রাই সহ।',
    restaurant: 'ফুড কর্নার'
  },
  {
    id: 14,
    name: 'সিঙ্গারা',
    englishName: 'Singara',
    category: 'নাস্তা ও ক্ষুধাবর্ধক',
    store: 'মামার হোটেল',
    price: '৳২০.০০',
    image: '/images/foods/singara1.png',
    description: 'মচমচে ময়দার খোসায় আলু ও মসলার মিশ্রণ দিয়ে ভর্তি। চায়ের সাথে নাস্তার জন্য উপযুক্ত।',
    restaurant: 'মামার হোটেল'
  },
  {
    id: 15,
    name: 'ভাত',
    englishName: 'Plain Rice',
    category: 'ভাতের খাবার',
    store: 'বাংলা রান্নাঘর',
    price: '৪০.০০',
    image: '/images/foods/rice1.png',
    description: 'সেদ্ধ করা সাদা ভাত। সব ধরনের তরকারি ও ডালের সাথে খাওয়ার জন্য আদর্শ।',
    restaurant: 'বাংলা রান্নাঘর'
  },
  {
    id: 16,
    name: 'মুরগির রোস্ট',
    englishName: 'Chicken Roast',
    category: 'মুরগির খাবার',
    store: 'পপুলার ক্যাটারিং',
    price: '৩০০.০০',
    image: '/images/foods/roast1.png',
    description: 'মসলা মাখিয়ে ওভেনে রোস্ট করা পূর্ণ মুরগি। বিশেষ অনুষ্ঠানের জন্য উপযুক্ত।',
    restaurant: 'পপুলার ক্যাটারিং'
  },
  {
    id: 17,
    name: 'ফুচকা',
    englishName: 'Fuchka',
    category: 'নাস্তা ও ক্ষুধাবর্ধক',
    store: 'স্বাদ রেস্তোরাঁ',
    price: '৫০.০০',
    image: '/images/foods/fuchka1.png',
    description: 'মচমচে পুরি তেঁতুলের জল ও মসলার সাথে। বাংলাদেশের জনপ্রিয় স্ট্রিট ফুড।',
    restaurant: 'স্বাদ রেস্তোরাঁ'
  },
  {
    id: 18,
    name: 'লস্সি',
    englishName: 'Lassi',
    category: 'পানীয়',
    store: 'রসগোল্লা রেস্তোরাঁ',
    price: '৮০.০০',
    image: '/images/foods/lassi1.png',
    description: 'দই ও চিনি দিয়ে তৈরি ঠান্ডা পানীয়। গরমের দিনে শরীর ঠান্ডা রাখতে চমৎকার।',
    restaurant: 'রসগোল্লা রেস্তোরাঁ'
  },
  {
    id: 19,
    name: 'ফিশ ফ্রাই',
    englishName: 'Fish Fry',
    category: 'মাছ ও সামুদ্রিক খাবার',
    store: 'ফুড কর্নার',
    price: '২২০.০০',
    image: '/images/foods/fishfry1.png',
    description: 'মসলা মাখিয়ে ডিপ ফ্রাই করা তাজা মাছ। ভাত ও সালাদের সাথে পরিবেশিত।',
    restaurant: 'ফুড কর্নার'
  },
  {
    id: 20,
    name: 'খিচুড়ি',
    englishName: 'Khichuri',
    category: 'ভাতের খাবার',
    store: 'দেশী খাবার ঘর',
    price: '১২০.০০',
    image: '/images/foods/khichuri1.png',
    description: 'চাল ও ডাল একসাথে রান্না করা পুষ্টিকর খাবার। সবজি ও মসলা দিয়ে স্বাদযুক্ত।',
    restaurant: 'দেশী খাবার ঘর'
  }
];

// All 64 districts of Bangladesh with both Bangla and English names
const districts = [
  { bn: 'সকল জেলা', en: 'All Districts' },
  { bn: 'ঢাকা', en: 'Dhaka' },
  { bn: 'ফরিদপুর', en: 'Faridpur' },
  { bn: 'গাজীপুর', en: 'Gazipur' },
  { bn: 'গোপালগঞ্জ', en: 'Gopalganj' },
  { bn: 'জামালপুর', en: 'Jamalpur' },
  { bn: 'কিশোরগঞ্জ', en: 'Kishoreganj' },
  { bn: 'মাদারীপুর', en: 'Madaripur' },
  { bn: 'মানিকগঞ্জ', en: 'Manikganj' },
  { bn: 'মুন্সিগঞ্জ', en: 'Munshiganj' },
  { bn: 'ময়মনসিংহ', en: 'Mymensingh' },
  { bn: 'নারায়ণগঞ্জ', en: 'Narayanganj' },
  { bn: 'নরসিংদী', en: 'Narsingdi' },
  { bn: 'নেত্রকোনা', en: 'Netrokona' },
  { bn: 'রাজবাড়ী', en: 'Rajbari' },
  { bn: 'শরীয়তপুর', en: 'Shariatpur' },
  { bn: 'শেরপুর', en: 'Sherpur' },
  { bn: 'টাঙ্গাইল', en: 'Tangail' },
  { bn: 'বগুড়া', en: 'Bogura' },
  { bn: 'জয়পুরহাট', en: 'Joypurhat' },
  { bn: 'নওগাঁ', en: 'Naogaon' },
  { bn: 'নাটোর', en: 'Natore' },
  { bn: 'চাঁপাইনবাবগঞ্জ', en: 'Chapainawabganj' },
  { bn: 'পাবনা', en: 'Pabna' },
  { bn: 'রাজশাহী', en: 'Rajshahi' },
  { bn: 'সিরাজগঞ্জ', en: 'Sirajganj' },
  { bn: 'দিনাজপুর', en: 'Dinajpur' },
  { bn: 'গাইবান্ধা', en: 'Gaibandha' },
  { bn: 'কুড়িগ্রাম', en: 'Kurigram' },
  { bn: 'লালমনিরহাট', en: 'Lalmonirhat' },
  { bn: 'নীলফামারী', en: 'Nilphamari' },
  { bn: 'পঞ্চগড়', en: 'Panchagarh' },
  { bn: 'রংপুর', en: 'Rangpur' },
  { bn: 'ঠাকুরগাঁও', en: 'Thakurgaon' },
  { bn: 'বরগুনা', en: 'Barguna' },
  { bn: 'বরিশাল', en: 'Barishal' },
  { bn: 'ভোলা', en: 'Bhola' },
  { bn: 'ঝালকাঠি', en: 'Jhalokati' },
  { bn: 'পটুয়াখালী', en: 'Patuakhali' },
  { bn: 'পিরোজপুর', en: 'Pirojpur' },
  { bn: 'বান্দরবান', en: 'Bandarban' },
  { bn: 'ব্রাহ্মণবাড়িয়া', en: 'Brahmanbaria' },
  { bn: 'চাঁদপুর', en: 'Chandpur' },
  { bn: 'চট্টগ্রাম', en: 'Chattogram' },
  { bn: 'কুমিল্লা', en: 'Cumilla' },
  { bn: 'কক্সবাজার', en: 'Cox\'s Bazar' },
  { bn: 'ফেনী', en: 'Feni' },
  { bn: 'খাগড়াছড়ি', en: 'Khagrachhari' },
  { bn: 'লক্ষ্মীপুর', en: 'Lakshmipur' },
  { bn: 'নোয়াখালী', en: 'Noakhali' },
  { bn: 'রাঙ্গামাটি', en: 'Rangamati' },
  { bn: 'হবিগঞ্জ', en: 'Habiganj' },
  { bn: 'মৌলভীবাজার', en: 'Moulvibazar' },
  { bn: 'সুনামগঞ্জ', en: 'Sunamganj' },
  { bn: 'সিলেট', en: 'Sylhet' },
  { bn: 'বাগেরহাট', en: 'Bagerhat' },
  { bn: 'চুয়াডাঙ্গা', en: 'Chuadanga' },
  { bn: 'যশোর', en: 'Jashore' },
  { bn: 'ঝিনাইদহ', en: 'Jhenaidah' },
  { bn: 'খুলনা', en: 'Khulna' },
  { bn: 'কুষ্টিয়া', en: 'Kushtia' },
  { bn: 'মাগুরা', en: 'Magura' },
  { bn: 'মেহেরপুর', en: 'Meherpur' },
  { bn: 'নড়াইল', en: 'Narail' },
  { bn: 'সাতক্ষীরা', en: 'Satkhira' }
];

// Mock upazillas data with district associations
const upazillasByDistrict = {
  'ঢাকা': [
    { bn: 'সকল উপজেলা', en: 'All Upazilas' },
    { bn: 'ধানমন্ডি', en: 'Dhanmondi' },
    { bn: 'মিরপুর', en: 'Mirpur' },
    { bn: 'মোহাম্মদপুর', en: 'Mohammadpur' },
    { bn: 'গুলশান', en: 'Gulshan' },
    { bn: 'বনানী', en: 'Banani' },
    { bn: 'উত্তরা', en: 'Uttara' },
    { bn: 'রমনা', en: 'Ramna' },
    { bn: 'মতিঝিল', en: 'Motijheel' },
    { bn: 'খিলগাঁও', en: 'Khilgaon' },
    { bn: 'সাভার', en: 'Savar' },
    { bn: 'কেরাণীগঞ্জ', en: 'Keraniganj' },
    { bn: 'ধামরাই', en: 'Dhamrai' },
    { bn: 'দোহার', en: 'Dohar' },
    { bn: 'নবাবগঞ্জ', en: 'Nawabganj' }
  ],
  'চট্টগ্রাম': [
    { bn: 'সকল উপজেলা', en: 'All Upazilas' },
    { bn: 'আগ্রাবাদ', en: 'Agrabad' },
    { bn: 'চান্দগাঁও', en: 'Chandgaon' },
    { bn: 'হালিশহর', en: 'Halishahar' },
    { bn: 'পতেঙ্গা', en: 'Patenga' },
    { bn: 'বায়েজিদ', en: 'Bayejid' },
    { bn: 'সীতাকুন্ড', en: 'Sitakunda' },
    { bn: 'মিরসরাই', en: 'Mirsharai' },
    { bn: 'পটিয়া', en: 'Patiya' },
    { bn: 'সন্দ্বীপ', en: 'Sandwip' },
    { bn: 'আনোয়ারা', en: 'Anwara' },
    { bn: 'চন্দনাইশ', en: 'Chandanaish' },
    { bn: 'সাতকানিয়া', en: 'Satkania' },
    { bn: 'লোহাগাড়া', en: 'Lohagara' },
    { bn: 'বাঁশখালী', en: 'Banshkhali' },
    { bn: 'বোয়ালখালী', en: 'Boalkhali' }
  ],
  // Add more districts with their upazillas as needed
};

// Add a default set of upazillas for districts we haven't fully mapped
const defaultUpazillas = [
  { bn: 'সকল উপজেলা', en: 'All Upazilas' },
  { bn: 'সদর উপজেলা', en: 'Sadar Upazila' },
  { bn: 'উপশহর', en: 'Upashahar' },
  { bn: 'পৌরসভা', en: 'Pourashava' }
];

// Default upazillas for "All Districts" option
const allUpazillas = [
  { bn: 'সকল উপজেলা', en: 'All Upazilas' },
  { bn: 'ধানমন্ডি', en: 'Dhanmondi' },
  { bn: 'মিরপুর', en: 'Mirpur' },
  { bn: 'গুলশান', en: 'Gulshan' },
  { bn: 'উত্তরা', en: 'Uttara' },
  { bn: 'মোহাম্মদপুর', en: 'Mohammadpur' },
  { bn: 'বনানী', en: 'Banani' },
  { bn: 'সিলেট সদর', en: 'Sylhet Sadar' },
  { bn: 'চট্টগ্রাম সদর', en: 'Chattogram Sadar' },
  { bn: 'খুলনা সদর', en: 'Khulna Sadar' },
  { bn: 'রাজশাহী সদর', en: 'Rajshahi Sadar' }
];

// List of bazars in Bangla
const bazars = [
  { bn: 'সকল বাজার', en: 'All Bazars' },
  { bn: 'চাচকৈড় বাজার', en: 'Chachkoir Bazar' },
  { bn: 'খুবজিপুর বাজার', en: 'Khubipur Bazar' },
  { bn: 'নাজিরপুর বাজার', en: 'Nazirpur Bazar' },
  { bn: 'বিলদোহর বাজার', en: 'Bildohar Bazar' },
  { bn: 'চাটখিল বাজার', en: 'Chatkhil Bazar' },
  { bn: 'সোনাইমুড়ি বাজার', en: 'Sonaimuri Bazar' },
  { bn: 'কেন্দ্রীয় বাজার', en: 'Central Bazar' },
  { bn: 'নিউ মার্কেট', en: 'New Market' },
  { bn: 'গুলিস্তান বাজার', en: 'Gulistan Bazar' },
  { bn: 'মৌচাক বাজার', en: 'Mouchak Bazar' },
  { bn: 'রকেট বাজার', en: 'Rocket Bazar' },
  { bn: 'কাওরান বাজার', en: 'Kawran Bazar' },
  { bn: 'ফার্মগেট বাজার', en: 'Farmgate Bazar' },
  { bn: 'মিরপুর বাজার', en: 'Mirpur Bazar' },
  { bn: 'উত্তরা বাজার', en: 'Uttara Bazar' },
  { bn: 'ধানমন্ডি বাজার', en: 'Dhanmondi Bazar' },
  { bn: 'মোহাম্মদপুর বাজার', en: 'Mohammadpur Bazar' },
  { bn: 'বনানী বাজার', en: 'Banani Bazar' },
  { bn: 'গুলশান বাজার', en: 'Gulshan Bazar' }
];

const FoodBazar = () => {
  const { addToCart } = useCart();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreTab, setSelectedStoreTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [selectedUpazilla, setSelectedUpazilla] = useState('সকল উপজেলা');
  const [selectedDistrict, setSelectedDistrict] = useState('সকল জেলা');
  const [selectedBazar, setSelectedBazar] = useState('সকল বাজার');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [districtSearchQuery, setDistrictSearchQuery] = useState('');
  const [upazillaSearchQuery, setUpazillaSearchQuery] = useState('');
  const [bazarSearchQuery, setBazarSearchQuery] = useState('');
  const [typingText, setTypingText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [expandedCategories, setExpandedCategories] = useState({});

  // State for filtered upazillas based on selected district
  const [availableUpazillas, setAvailableUpazillas] = useState(allUpazillas);

  const searchPlaceholders = [
    'খাবারের নাম লিখুন...',
    'রেস্তোরাঁর নাম লিখুন...',
    'খাবারের ধরন লিখুন...',
    'প্রিয় খাবার খুঁজুন...',
    'দোকানের নাম লিখুন...'
  ];

  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  // Typewriter effect for search placeholders
  useEffect(() => {
    const currentPlaceholder = searchPlaceholders[placeholderIndex];
    
    // Handle typing or deleting effect
    const handleTyping = () => {
      // If currently deleting text
      if (isDeleting) {
        // Set text to the substring minus the last character
        setTypingText(prev => prev.slice(0, -1));
        setTypingSpeed(50); // Faster speed when deleting
        
        // When text is fully deleted, start typing next placeholder
        if (typingText === '') {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % searchPlaceholders.length);
          setTypingSpeed(150); // Normal speed for typing
        }
      } 
      // If currently typing text
      else {
        // Add next character to current text
        setTypingText(currentPlaceholder.slice(0, typingText.length + 1));
        setTypingSpeed(150); // Normal speed for typing
        
        // When text is fully typed, pause before deleting
        if (typingText === currentPlaceholder) {
          // Pause at the end of typing before starting to delete
          setTypingSpeed(2000); // Longer pause before deleting
          setIsDeleting(true);
        }
      }
    };
    
    // Set up timer for typing/deleting animation
    const timer = setTimeout(handleTyping, typingSpeed);
    
    // Clean up the timer
    return () => clearTimeout(timer);
  }, [typingText, isDeleting, placeholderIndex, searchPlaceholders, typingSpeed]);

  const handleCategoryClick = (categoryKey) => {
    // Toggle expanded state for this category
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSubCategoryClick = (mainCategoryKey, subCategory) => {
    setSelectedCategory(mainCategoryKey);
    setSelectedSubCategory(subCategory);
  };

  const handleMainCategorySelect = (categoryKey) => {
    if (selectedCategory === categoryKey) {
      setSelectedCategory('');
      setSelectedSubCategory('');
    } else {
      setSelectedCategory(categoryKey);
      setSelectedSubCategory('');
    }
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store);
    setSelectedStoreTab(0); // Reset tab to "All" when selecting a new store
  };

  const handlePrescriptionUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPrescriptionFile(file);
      // Here you would typically upload the file to your server
      // and process the prescription
    }
  };

  const handleUpazillaChange = (event) => {
    setSelectedUpazilla(event.target.value);
  };

  const handleBazarChange = (event) => {
    setSelectedBazar(event.target.value);
  };

  const handleAddToCart = (product) => {
    console.log('FoodBazar - Adding product to cart:', product);
    
    const cartProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      store: product.store,
      stock: product.stock || 10
    };
    
    addToCart(cartProduct, 1);
  };

  const handleDistrictChange = (event) => {
    const district = event.target.value;
    setSelectedDistrict(district);
    
    // Reset upazilla when district changes
    setSelectedUpazilla('সকল উপজেলা');
    setUpazillaSearchQuery('');
    
    // Update available upazillas based on selected district
    if (district === 'সকল জেলা') {
      setAvailableUpazillas(allUpazillas);
    } else {
      // Look up the district's upazillas, or use defaults if not specifically mapped
      const districtUpazillas = upazillasByDistrict[district] || defaultUpazillas;
      setAvailableUpazillas(districtUpazillas);
    }
  };

  // Filter districts based on search query
  const filteredDistricts = districts.filter(
    district => district.bn.toLowerCase().includes(districtSearchQuery.toLowerCase())
  );

  // Filter upazillas based on search query
  const filteredUpazillas = availableUpazillas.filter(
    upazilla => upazilla.bn.toLowerCase().includes(upazillaSearchQuery.toLowerCase())
  );

  // Filter bazars based on search query
  const filteredBazars = bazars.filter(
    bazar => bazar.bn.toLowerCase().includes(bazarSearchQuery.toLowerCase())
  );

  // Custom dropdown render props
  const districtMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      },
    },
  };

  const upazillaMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      },
    },
  };

  // Get unique categories for the selected store
  const getStoreCategoriesWithCounts = () => {
    if (!selectedStore) return [];
    
    const storeProducts = foods.filter(food => food.store === selectedStore.name);
    const categoryMap = new Map();
    
    // Count foods in each category
    storeProducts.forEach(food => {
      const count = categoryMap.get(food.category) || 0;
      categoryMap.set(food.category, count + 1);
    });
    
    // Convert to array and add "All" option
    const categories = [
      { name: 'সকল খাবার', count: storeProducts.length },
      ...Array.from(categoryMap.entries()).map(([name, count]) => ({ name, count }))
    ];
    
    return categories;
  };

  const filteredFoods = foods.filter((food) => {
    const matchesCategory = !selectedCategory || 
      (selectedSubCategory ? food.category === selectedSubCategory : 
       restaurantFoods.find(cat => cat.category.bangla === selectedCategory)?.items.some(item => item.bangla === food.category));
    const matchesStore = !selectedStore || food.store === selectedStore.name;
    
    // Add store tab filtering
    let matchesStoreTab = true;
    if (selectedStore && selectedStoreTab > 0) {
      const storeCategories = getStoreCategoriesWithCounts();
      const selectedTabCategory = storeCategories[selectedStoreTab]?.name;
      matchesStoreTab = food.category === selectedTabCategory;
    }
    
    const matchesSearch = !searchQuery || 
      food.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      food.englishName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStore && matchesStoreTab && matchesSearch;
  });

  const handleStoreTabChange = (event, newValue) => {
    setSelectedStoreTab(newValue);
  };

  return (
    <>
      <Header />
      <Box className="food-bazar-container" sx={{ flexGrow: 1, p: 2 }}>
        {/* New Search Header Bar */}
        <SearchBarHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/food.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />
          
          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              // Optional: Handle location change if needed
              console.log('Location changed:', location);
            }}
          />

          {/* Animated Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={typingText}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: 2, 
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              zIndex: 2,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ 
                    opacity: isDeleting ? 0 : 1, 
                    transition: 'opacity 0.3s',
                    height: '1.2em', 
                    width: '0.15em', 
                    backgroundColor: 'primary.main',
                    display: 'inline-block',
                    animation: 'blink 1s step-end infinite'
                  }}/>
                </InputAdornment>
              )
            }}
          />

          {/* Recipe Upload Button */}
          <Box sx={{ zIndex: 2 }}>
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={handlePrescriptionUpload}
              sx={{ display: 'none' }}
              id="quick-recipe-upload"
            />
            <label htmlFor="quick-recipe-upload">
              <Tooltip title="রেসিপি আপলোড করুন">
                <Button 
                  variant="contained" 
                  component="span" 
                  startIcon={<Upload />}
                  sx={{ 
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #f5f5f5 30%, #ffffff 90%)',
                    color: '#5e35b1',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 20px 0px rgba(0, 0, 0, 0.14)',
                    height: '56px',
                    minWidth: '200px',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #ffffff 30%, #f5f5f5 90%)',
                      boxShadow: '0 6px 20px 0px rgba(0, 0, 0, 0.18)',
                    }
                  }}
                >
                  রেসিপি আপলোড
                </Button>
              </Tooltip>
            </label>
          </Box>
        </SearchBarHeader>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 } 
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <Restaurant sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    খাবারের ধরন
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {restaurantFoods.map((categoryData, index) => {
                    const categoryKey = categoryData.category.bangla;
                    return (
                      <React.Fragment key={categoryKey}>
                        <ListItem
                          button
                          onClick={() => handleCategoryClick(categoryKey)}
                          selected={selectedCategory === categoryKey}
                          sx={{ py: 1 }}
                        >
                          <ListItemIcon>
                            {categoryData.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={categoryData.category.bangla}
                            secondary={categoryData.category.english}
                            primaryTypographyProps={{ 
                              style: { 
                                fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                                fontSize: '0.9rem'
                              } 
                            }}
                            secondaryTypographyProps={{ 
                              style: { 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                              } 
                            }}
                          />
                          {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categoryData.items.map((item) => (
                              <ListItem
                                button
                                key={`${categoryKey}-${item.bangla}`}
                                sx={{ pl: 4, py: 0.5 }}
                                selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                                onClick={() => handleSubCategoryClick(categoryKey, item.bangla)}
                              >
                                <ListItemIcon>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.bangla}
                                  secondary={item.english}
                                  primaryTypographyProps={{ 
                                    style: { 
                                      fontSize: '0.8rem',
                                      fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                    } 
                                  }}
                                  secondaryTypographyProps={{ 
                                    style: { fontSize: '0.7rem' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    খাবারের দোকান
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {stores.map((store) => (
                    <ListItem
                      button
                      key={store.name}
                      selected={selectedStore?.name === store.name}
                      onClick={() => handleStoreClick(store)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Box
                            component="img"
                            src={store.logo}
                            alt={store.name}
                            sx={{
                              width: 20,
                              height: 20,
                              objectFit: 'contain',
                              borderRadius: 1,
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            onLoad={(e) => {
                              e.target.style.display = 'block';
                              e.target.nextSibling.style.display = 'none';
                            }}
                          />
                          <DefaultRestaurantLogo sx={{ display: 'none' }}>
                            <Restaurant sx={{ fontSize: 12, color: '#1976d2' }} />
                          </DefaultRestaurantLogo>
                        </Box>
                      </ListItemIcon>
                      <ListItemText 
                        primary={store.name}
                        secondary={`${store.englishName} • ${store.description}`}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedStore?.name === store.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Store Details Section */}
              {selectedStore && (
                <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(45deg, #f8f9fa 30%, #e3f2fd 90%)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      backgroundColor: '#e3f2fd',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #bbdefb',
                      mr: 3
                    }}>
                      <Restaurant sx={{ fontSize: 30, color: '#1976d2' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        {selectedStore.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                        {selectedStore.englishName}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedStore.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOn sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            সেবা এলাকা: সারাদেশ
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Restaurant sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            খাবারের সংখ্যা: {filteredFoods.length}টি
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setSelectedStore(null)}
                      startIcon={<Close />}
                      sx={{ ml: 2 }}
                    >
                      বন্ধ করুন
                    </Button>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#1976d2', mb: 1 }}>
                    {selectedStore.name} এর খাবারসমূহ
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    নিচে {selectedStore.name} রেস্তোরাঁর সকল উপলব্ধ খাবার দেখানো হয়েছে। আপনি চাইলে ক্যাটেগরি অনুযায়ী ফিল্টার করতে পারেন।
                  </Typography>
                  
                  {/* Category Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs 
                      value={selectedStoreTab} 
                      onChange={handleStoreTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        '& .MuiTab-root': {
                          minWidth: 'auto',
                          px: 2,
                          py: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#1976d2',
                        }
                      }}
                    >
                      {getStoreCategoriesWithCounts().map((category, index) => (
                        <Tab 
                          key={index}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                                {category.name}
                              </Typography>
                              <Box sx={{ 
                                backgroundColor: selectedStoreTab === index ? '#1976d2' : '#e0e0e0',
                                color: selectedStoreTab === index ? 'white' : '#666',
                                borderRadius: '12px',
                                px: 1,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                minWidth: '20px',
                                textAlign: 'center'
                              }}>
                                {category.count}
                              </Box>
                            </Box>
                          }
                          sx={{
                            color: selectedStoreTab === index ? '#1976d2' : 'text.secondary',
                            '&.Mui-selected': {
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>
                </Paper>
              )}

              {/* Food Grid */}
              {filteredFoods.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 5 cards per row
                      lg: 'repeat(6, 1fr)', // Big screen: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredFoods.map((food) => (
                    <Box key={food.id} sx={{ display: 'flex', width: '100%' }}>
                      <FoodCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <Link 
                            to={`/food-bazar/product/${food.id}`} 
                            style={{ textDecoration: 'none' }}
                          >
                            <CardMedia
                              component="img"
                              height="160"
                              image={food.image}
                              alt={food.name}
                              sx={{ 
                                objectFit: 'contain', 
                                backgroundColor: '#f5f5f5', 
                                pt: 1,
                                cursor: 'pointer',
                                transition: 'transform 0.3s ease-in-out',
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                            />
                          </Link>
                          <StyledBadge>{food.category}</StyledBadge>
                        </Box>
                        <CardContent sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          pb: '16px !important' // Override default padding bottom
                        }}>
                          <Box>
                            <Link 
                              to={`/food-bazar/product/${food.id}`}
                              style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                              <Typography 
                                gutterBottom 
                                variant="h6" 
                                component="div"
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    color: 'primary.main'
                                  }
                                }}
                              >
                                {food.name}
                              </Typography>
                            </Link>
                            <Typography variant="body2" color="text.secondary">
                              {food.englishName}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.primary">
                                <strong>রেস্টুরেন্ট:</strong> {food.restaurant}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>রেটিং:</strong> {food.rating}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{
                            mt: 'auto', // Push this box to the bottom
                            pt: 2 // Add some spacing from the content above
                          }}>
                            <Typography variant="h6" color="primary" sx={{ mb: 1, textAlign: 'center' }}>
                              {food.price}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1
                            }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                fullWidth
                              >
                                কিনুন
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                onClick={() => handleAddToCart(food)}
                              >
                                ব্যাগে নিন
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </FoodCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                  <Restaurant sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    কোন খাবার পাওয়া যায়নি
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    অনুগ্রহ করে আপনার ফিল্টার বা সার্চ পরিবর্তন করুন
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedSubCategory('');
                      setSelectedStore('');
                      setSearchQuery('');
                      setSelectedBazar('সকল বাজার');
                      setExpandedCategories({});
                    }}
                  >
                    সব ফিল্টার মুছুন
                  </Button>
                </Paper>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default FoodBazar;
